/// <reference path="../types/shims.d.ts" />
import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'wouter';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useQuery } from '@tanstack/react-query';
// Type shim is provided in client/src/types/shims.d.ts
import Plot from 'react-plotly.js';
import { Share, Clock, AlertTriangle, Calendar, BarChart } from 'lucide-react';
// import type { Plot as PlotType } from '@shared/schema';
import { LumiLogoRound } from '@/components/icons/lumi-logo-round';
import { trackClient } from '@/lib/obs';
import { FigureCanvas } from '@/components/figure-composition/FigureCanvas';
import { useFigureCompositionStore } from '@/store/figure-composition';
import { getMapboxAccessToken } from '@/lib/plotly-mapbox-config';

export default function SharedPlot() {
  const { token } = useParams();
  const [isExpired, setIsExpired] = useState(false);
  const { data: plot, isLoading, error } = useQuery<any>({
    queryKey: ['/api/share', token],
    enabled: !!token,
    retry: false,
  });

  useEffect(() => {
    if (error && (error as any).message?.includes('410')) {
      setIsExpired(true);
    }
  }, [error]);

  // Check if this is a figure share or plot share
  const isFigureShare = (plot as any)?.type === 'figure';
  const figureComposition = (plot as any)?.figureComposition;
  
  // Debug: log what we received from the API
  useEffect(() => {
    if (isFigureShare && figureComposition) {
      console.log('[SharedPlot] Received figure composition:', {
        hasComposition: !!figureComposition,
        caption: figureComposition.caption?.substring(0, 100),
        captionManual: figureComposition.captionManual?.substring(0, 100),
        captionAuto: figureComposition.captionAuto?.substring(0, 100),
        panelCount: figureComposition.panels?.length || 0,
        panelsWithHideFlags: figureComposition.panels?.map((p: any) => ({
          id: p.id,
          plotId: p.plotId,
          hideTitle: p.layoutOverrides?.hideTitle,
          hideSubtitle: p.layoutOverrides?.hideSubtitle,
          hideAnnotations: p.layoutOverrides?.hideAnnotations,
        })),
        allKeys: Object.keys(figureComposition || {})
      });
    }
  }, [isFigureShare, figureComposition]);

  // Initialize store with shared composition SYNCHRONOUSLY before rendering Panel components
  // This ensures Panel hooks can access the store immediately
  if (isFigureShare && figureComposition && !useFigureCompositionStore.getState().currentComposition) {
    const compositionWithDefaults = {
      ...figureComposition,
      id: figureComposition.id || 'shared',
      projectId: figureComposition.projectId || 'shared',
      createdAt: figureComposition.createdAt ? new Date(figureComposition.createdAt) : new Date(),
      updatedAt: figureComposition.updatedAt ? new Date(figureComposition.updatedAt) : new Date(),
    };
    useFigureCompositionStore.setState({ currentComposition: compositionWithDefaults });
    console.log('[SharedPlot] Initialized store with shared figure composition:', compositionWithDefaults);
  }

  // Also update in useEffect to handle changes
  useEffect(() => {
    if (isFigureShare && figureComposition) {
      const compositionWithDefaults = {
        ...figureComposition,
        id: figureComposition.id || 'shared',
        projectId: figureComposition.projectId || 'shared',
        createdAt: figureComposition.createdAt ? new Date(figureComposition.createdAt) : new Date(),
        updatedAt: figureComposition.updatedAt ? new Date(figureComposition.updatedAt) : new Date(),
      };
      useFigureCompositionStore.setState({ currentComposition: compositionWithDefaults });
    }
  }, [isFigureShare, figureComposition]);

  // Get effective caption for figure compositions
  const figureCaption = useMemo(() => {
    if (!isFigureShare || !figureComposition) return null;
    // Use captionManual if available, otherwise captionAuto, otherwise legacy caption
    const caption = figureComposition.captionManual || 
                   figureComposition.captionAuto || 
                   figureComposition.caption || 
                   '';
    const trimmed = caption.trim();
    console.log('[SharedPlot] Caption extraction:', {
      hasComposition: !!figureComposition,
      captionManual: figureComposition?.captionManual?.substring(0, 50),
      captionAuto: figureComposition?.captionAuto?.substring(0, 50),
      caption: figureComposition?.caption?.substring(0, 50),
      finalCaption: trimmed?.substring(0, 50)
    });
    return trimmed || null;
  }, [isFigureShare, figureComposition]);
  
  // Compute themed data/layout before any conditional returns to keep hook order stable
  const svgInline: string | undefined = (plot as any)?.render?.svg_inline;
  const plotData: any[] = (plot as any)?.plotlyJson?.data || [];
  const plotLayout: any = (plot as any)?.plotlyJson?.layout || {};
  const plotFrames: any[] | undefined = (plot as any)?.plotlyJson?.frames;

  const { themedData, themedLayout } = useMemo(() => {
    try {
      const layout = { ...(plotLayout || {}) } as any;
      const theme = (layout?.meta as any)?.lumi?.theme || {};
      const categorical: string[] | undefined = Array.isArray(theme.categorical) ? theme.categorical : undefined;
      const continuous: string = typeof theme.continuous === 'string' && theme.continuous.length > 0 ? theme.continuous : 'Viridis';
      const shareColoraxis: boolean = typeof (theme as any)?.coloraxis === 'string' && String((theme as any).coloraxis).length > 0;
      const coloraxisId: string = (shareColoraxis ? (theme as any).coloraxis : 'coloraxis') as string;

      if (categorical && categorical.length > 0) {
        if (!layout.colorway) layout.colorway = categorical;
        if (!layout.sunburstcolorway) layout.sunburstcolorway = categorical;
        if (!layout.treemapcolorway) layout.treemapcolorway = categorical;
        if (!layout.iciclecolorway) layout.iciclecolorway = categorical;
        if (!layout.funnelareacolorway) layout.funnelareacolorway = categorical;
      }

      try {
        Object.keys(layout).forEach((k: string) => {
          if (k === 'coloraxis' || /^coloraxis\d+$/.test(k)) {
            const ca: any = (layout as any)[k];
            if (ca && ca.colorscale === undefined) {
              (layout as any)[k] = { ...ca, colorscale: continuous };
            }
          }
        });
        if (shareColoraxis) {
          const ca: any = (layout as any)[coloraxisId] || {};
          (layout as any)[coloraxisId] = { ...ca, colorscale: (layout as any)[coloraxisId]?.colorscale || continuous };
        }
      } catch {}

      const isNumericArray = (arr: any): boolean => Array.isArray(arr) && arr.length > 0 && arr.every((v) => typeof v === 'number' && isFinite(v));
      const hasZ = (tr: any) => tr && tr.z && (Array.isArray(tr.z) || Array.isArray(tr.z?.[0]));

      const data = Array.isArray(plotData) ? plotData.map((tr: any, traceIndex: number) => {
        const next: any = { ...tr };
        // Prevent clipping of text labels at axis boundaries
        next.cliponaxis = false;
        const t = String((next.type || 'scatter') as string).toLowerCase();

        const zScaleTypes = new Set(['heatmap', 'contour', 'histogram2d', 'histogram2dcontour', 'densitymapbox', 'choropleth', 'choroplethmapbox', 'image']);
        if (zScaleTypes.has(t)) {
          if (hasZ(next)) {
            if (shareColoraxis) {
              next.coloraxis = coloraxisId;
              delete (next as any).colorscale;
              delete (next as any).autocolorscale;
            } else {
              delete (next as any).colorscale;
              delete (next as any).autocolorscale;
              next.colorscale = continuous;
            }
          }
          if (typeof next.opacity === 'number') {
            const n:any = { ...next }; delete n.opacity; return n;
          }
        }

        const markerScaleTypes = new Set(['scatter', 'scattergl', 'scattergeo', 'scattermapbox', 'splom', 'bar']);
        if (markerScaleTypes.has(t)) {
          if (next.marker && isNumericArray(next.marker.color)) {
            if (shareColoraxis) {
              next.marker = { ...next.marker, coloraxis: coloraxisId } as any;
              delete (next.marker as any).colorscale;
              delete (next.marker as any).autocolorscale;
            } else {
              delete (next.marker as any).colorscale;
              delete (next.marker as any).autocolorscale;
              next.marker = { ...next.marker, colorscale: continuous };
            }
          }
        }

        if (t === 'parcats') {
          if (next.line && typeof next.line === 'object' && isNumericArray(next.line.color)) {
            delete (next.line as any).colorscale;
            next.line = { ...next.line, colorscale: continuous };
          }
        }

        if (t === 'parcoords') {
          if (next.line && typeof next.line === 'object' && isNumericArray(next.line.color)) {
            delete (next.line as any).colorscale;
            next.line = { ...next.line, colorscale: continuous };
          }
        }

        if ((t === 'ohlc' || t === 'candlestick') && categorical && categorical.length >= 2) {
          const inc = next.increasing || {};
          const incLine = inc.line || {};
          const dec = next.decreasing || {};
          const decLine = dec.line || {};
          if (incLine.color === undefined) incLine.color = categorical[0];
          if (decLine.color === undefined) decLine.color = categorical[1 % categorical.length];
          next.increasing = { ...inc, line: incLine };
          next.decreasing = { ...dec, line: decLine };
        }

        if (t === 'waterfall' && categorical && categorical.length >= 3) {
          const inc = next.increasing || {};
          const incMarker = inc.marker || {};
          if (incMarker.color === undefined) incMarker.color = categorical[0];
          next.increasing = { ...inc, marker: incMarker };

          const dec = next.decreasing || {};
          const decMarker = dec.marker || {};
          if (decMarker.color === undefined) decMarker.color = categorical[1 % categorical.length];
          next.decreasing = { ...dec, marker: decMarker };

          const totals = next.totals || {};
          const totalsMarker = totals.marker || {};
          if (totalsMarker.color === undefined) totalsMarker.color = categorical[2 % categorical.length];
          next.totals = { ...totals, marker: totalsMarker };
        }

        const hierTypes = new Set(['treemap', 'sunburst', 'icicle']);
        if (hierTypes.has(t)) {
          if (next.marker && isNumericArray(next.marker.colors)) {
            if (shareColoraxis) {
              next.marker = { ...next.marker, coloraxis: coloraxisId } as any;
              delete (next.marker as any).colorscale;
              delete (next.marker as any).autocolorscale;
            } else {
              delete (next.marker as any).colorscale;
              delete (next.marker as any).autocolorscale;
              next.marker = { ...next.marker, colorscale: continuous };
            }
          }
        }

        if (t === 'sankey' && categorical && categorical.length > 0) {
          const node = next.node || {};
          const labels = node.labels || node.label;
          if (Array.isArray(labels)) {
            // Always apply theme colors to nodes (normalize all nodes)
            const nodeColors = labels.map((_: any, i: number) => categorical[i % categorical.length]);
            next.node = { ...node, color: nodeColors };
            
            // Always apply theme colors to links based on source node colors
            const link = next.link || {};
            if (Array.isArray(link.source) && Array.isArray(link.target)) {
              const linkColors = link.source.map((srcIdx: number) => {
                const srcColor = nodeColors[srcIdx] || categorical[srcIdx % categorical.length];
                // Add opacity to link colors
                if (srcColor.startsWith('#')) {
                  return srcColor + '80'; // 50% opacity
                } else if (srcColor.startsWith('rgba')) {
                  return srcColor;
                } else {
                  return srcColor + '80';
                }
              });
              next.link = { ...link, color: linkColors };
            }
          }
        }

        if (categorical && categorical.length > 0) {
          const defaultColor = categorical[traceIndex % categorical.length];
          if (t === 'bar' || t === 'histogram' || t === 'scatter' || t === 'scattergl' || t === 'splom' || t === 'scattergeo' || t === 'scattermapbox' || t === 'funnel') {
            if (!next.marker) next.marker = {};
            if (next.marker.color === undefined && next.marker.colorscale === undefined && next.marker.coloraxis === undefined && !isNumericArray(next.marker.color)) {
              next.marker.color = defaultColor;
            }
            if ((t === 'scatter' || t === 'scattergl') && next.mode && next.mode.includes('lines')) {
              if (!next.line) next.line = {};
              if (next.line.color === undefined) next.line.color = defaultColor;
            }
          } else if (t === 'box' || t === 'violin') {
            if (!next.line) next.line = {};
            if (next.line.color === undefined) next.line.color = defaultColor;
            if (next.fillcolor === undefined) next.fillcolor = defaultColor + '40';
          } else if (t === 'pie' || t === 'funnelarea') {
            const labels = next.labels || next.label;
            if (Array.isArray(labels)) {
              if (!next.marker) next.marker = {};
              if (next.marker.colors === undefined) {
                next.marker.colors = labels.map((_: any, i: number) => categorical[i % categorical.length]);
              }
            }
          }
        }

        return next;
      }) : plotData;

      try {
        if (shareColoraxis) {
          let inferredMin = Number.POSITIVE_INFINITY;
          let inferredMax = Number.NEGATIVE_INFINITY;
          if (Array.isArray(data)) {
            const isNumArr = (arr: any): boolean => Array.isArray(arr) && arr.length > 0 && arr.every((v) => typeof v === 'number' && isFinite(v));
            const hasZ2 = (tr: any) => tr && tr.z && (Array.isArray(tr.z) || Array.isArray(tr.z?.[0]));
            const zTypes = new Set(['heatmap','contour','histogram2d','histogram2dcontour','densitymapbox','choropleth','choroplethmapbox','image']);
            const mTypes = new Set(['scatter','scattergl','scattergeo','scattermapbox','splom','bar']);
            const hTypes = new Set(['treemap','sunburst','icicle']);
            for (const tr of data as any[]) {
              const tt = String((tr?.type || 'scatter')).toLowerCase();
              if (zTypes.has(tt) && hasZ2(tr)) {
                const z:any = tr.z;
                if (Array.isArray(z?.[0])) {
                  for (const row of z as any[]) for (const v of row as any[]) if (typeof v === 'number' && isFinite(v)) { if (v < inferredMin) inferredMin = v; if (v > inferredMax) inferredMax = v; }
                } else if (Array.isArray(z)) {
                  for (const v of z as any[]) if (typeof v === 'number' && isFinite(v)) { if (v < inferredMin) inferredMin = v; if (v > inferredMax) inferredMax = v; }
                }
              }
              if (mTypes.has(tt) && tr.marker && isNumArr(tr.marker.color)) {
                for (const v of tr.marker.color as any[]) if (typeof v === 'number' && isFinite(v)) { if (v < inferredMin) inferredMin = v; if (v > inferredMax) inferredMax = v; }
              }
              if (hTypes.has(tt) && tr.marker && isNumArr(tr.marker.colors)) {
                for (const v of tr.marker.colors as any[]) if (typeof v === 'number' && isFinite(v)) { if (v < inferredMin) inferredMin = v; if (v > inferredMax) inferredMax = v; }
              }
              if ((tt === 'parcoords' || tt === 'parcats') && tr.line && isNumArr(tr.line.color)) {
                for (const v of tr.line.color as any[]) if (typeof v === 'number' && isFinite(v)) { if (v < inferredMin) inferredMin = v; if (v > inferredMax) inferredMax = v; }
              }
            }
          }
          const ca:any = (layout as any)[coloraxisId] || {};
          if (isFinite(inferredMin) && isFinite(inferredMax)) {
            (layout as any)[coloraxisId] = { ...ca, cmin: ca.cmin ?? inferredMin, cmax: ca.cmax ?? inferredMax, cauto: ca.cauto ?? false };
          } else {
            (layout as any)[coloraxisId] = { ...ca };
          }
        }
      } catch {}

      return { themedData: data, themedLayout: layout };
    } catch {
      return { themedData: plotData, themedLayout: plotLayout } as any;
    }
  }, [plotData, plotLayout]);

  try { trackClient('share_view_opened', {}); } catch {}

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <h2 className="text-xl font-medium">Loading plot...</h2>
        </div>
      </div>
    );
  }

  if (isExpired) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md mx-4">
          <CardHeader className="text-left">
            <Clock className="h-16 w-16 text-muted-foreground mb-4" />
            <CardTitle>Share Link Expired</CardTitle>
          </CardHeader>
          <CardContent className="text-left">
            <p className="text-muted-foreground">
              This share link has expired or been revoked. Share links are valid for 7 days.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !plot) {
    const shareType = (plot as any)?.type === 'figure' ? 'figure' : 'plot';
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md mx-4">
          <CardHeader className="text-left">
            <AlertTriangle className="h-16 w-16 text-destructive mb-4" />
            <CardTitle>{shareType === 'figure' ? 'Figure Not Found' : 'Plot Not Found'}</CardTitle>
          </CardHeader>
          <CardContent className="text-left">
            <p className="text-muted-foreground">
              The shared {shareType} could not be found. It may have been deleted or the link is invalid.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="shared-plot-page">
      {/* Header */}
      <header className="bg-card border-b border-border px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Link href="/" aria-label="Go to Lumi home">
              <a className="inline-block" href="/">
                <LumiLogoRound size={32} />
              </a>
            </Link>
          </div>
        </div>
      </header>

      {/* Plot Content */}
      <div className="container mx-auto px-6 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Plot Info */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold mb-2" data-testid="plot-title">
              {plot.title}
            </h2>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              {plot.ownerName ? (
                <span className="flex items-center gap-1">
                  by {plot.ownerName}
                </span>
              ) : null}
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                Created {new Date((plot.createdAt as any) ?? Date.now()).toLocaleDateString()}
              </span>
              <span className="flex items-center gap-1">
                <BarChart className="h-3 w-3" />
                {(plot.plotSpec as any)?.plotType || 'Visualization'}
              </span>
            </div>
          </div>

          {/* View-only Render */}
          <Card className="mb-6">
            <CardContent className="p-6">
              {/* Show caption for regular plots */}
              {(plot as any)?.caption && !isFigureShare && (
                <p className="text-muted-foreground mb-4" data-testid="plot-caption">
                  {(plot as any).caption}
                </p>
              )}
              {/* Show caption for figure compositions */}
              {figureCaption && (
                <div className="mb-4">
                  <p className="text-muted-foreground whitespace-pre-line" data-testid="figure-caption">
                    {figureCaption}
                  </p>
                </div>
              )}
              <div className="w-full" data-testid="shared-plot">
                {isFigureShare && figureComposition ? (
                  <div className="w-full" style={{ minHeight: '60vh' }}>
                    <FigureCanvas 
                      composition={figureComposition}
                      showRulers={false}
                      previewAtJournalSize={false}
                      canvasZoom={1}
                      isReadOnly={true}
                    />
                  </div>
                ) : svgInline ? (
                  <div
                    className="w-full overflow-auto"
                    dangerouslySetInnerHTML={{ __html: svgInline }}
                  />
                ) : (
                  <div className="w-full h-[60vh] md:h-[70vh]" style={{ overflow: 'visible' }}>
                  <Plot
                    data={themedData}
                    frames={plotFrames}
                    layout={{
                      ...themedLayout,
                      autosize: true,
                      width: undefined, // Remove fixed width to allow responsive sizing
                      height: undefined, // Remove fixed height to allow responsive sizing
                      margin: {
                        t: Math.max(60, ((themedLayout as any)?.margin?.t ?? (((themedLayout as any)?.title?.text ? 100 : 60)))) as any,
                        r: Math.max(120, ((themedLayout as any)?.margin?.r ?? 120)) as any,
                        b: ((themedLayout as any)?.margin?.b ?? 60) as any,
                        l: Math.max(120, ((themedLayout as any)?.margin?.l ?? 120)) as any,
                      },
                    }}
                      config={{
                        displayModeBar: true,
                        modeBarButtonsToRemove: [
                          'pan2d',
                          'lasso2d', 
                          'select2d',
                          'toImage',
                          'sendDataToCloud',
                          'editInChartStudio'
                        ],
                        displaylogo: false,
                        responsive: true,
                        // Mapbox access token for scattermapbox, choroplethmapbox, densitymapbox
                        mapboxAccessToken: getMapboxAccessToken(),
                      }}
                      style={{ width: '100%', height: '100%' }}
                      useResizeHandler={true}
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Disclaimer */}
          <div className="bg-muted/50 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Share className="h-4 w-4" />
              <span>
                This is a read-only share. Downloads are disabled.
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
